import moment from 'moment'
import createSelector from 'lib/createSelector'
import config from 'constants/config'
import {
  GO_CARDLESS_PAYMENT_TYPE,
  MAX_LATITUDE_PAY_PAYMENT,
  MIN_LATITUDE_PAYMENT,
  PAYMENT_OPTIONS,
  PaymentMethod,
  PaypalSupplementaryData,
  RAZORPAY_PAYMENT_TYPE,
  STRIPE_PAYMENT_TYPE,
} from 'constants/payment'
import { isLatitudeFinanceEnabled, isLatitudeGemEnabled, isLatitudePayEnabled } from 'lib/latitudeUtils'
import { isKlarnaEnabled, isWithinKlarnaAvailabilityRange, KlarnaAttachment } from 'lib/klarnaUtils'
import { isKlarnaBpEnabled, isWithinKlarnaBpAvailabilityRange } from 'lib/klarnaBpUtils'
import { isApplePayEnabled } from 'lib/applePayUtils'
import { isGooglePayEnabled } from 'lib/googlePayUtils'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/afterPayBpUtils'
import {
  isStripe3dsEnabled,
  isStripe3dsForGiftCardsEnabled,
  isStripe3dsForStandaloneEnabled,
  isStripeBedbankRepeatCustomerCheckEnabled,
  isStripePaymentElementCardEnabled,
  isStripePaymentElementGiropayEnabled,
  isStripeWalletFlightsEnabled,
} from 'lib/stripeUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/hoolahBpUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/waaveBpUtils'
import { isPaypalEnabled } from 'lib/paymentUtils'
import getPaymentMethods from 'lib/getPaymentMethods'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import {
  getTourV2IdForCheckoutItem,
  isAccommodationItem,
  isBedbankItem,
  isCarHireItem,
  isCruiseItem,
  isExperienceItem,
  isFlightItem,
  isGiftCardItem,
  isInsuranceProtectionItem,
  isLEAccommodationItem,
  isLEHotelItem,
  isNoProtectionInsuranceItem,
  isTourV1Item,
  isTourV2Item,
} from 'lib/checkout'
import { beginningOfToday, dateDifference } from 'lib/dateUtils'
import { mapObject } from 'lib/objectUtils'
import { isBedbank, isLEOffer } from 'lib/offerTypes'
import { isStripeWalletDepositsEnabled } from 'lib/depositsUtils'
import { getExperienceItems, getExperienceItemsView } from 'checkout/selectors/view/experience'
import { checkoutTotalsView, getCreditPayableAmount } from 'checkout/selectors/payment/checkout'
import { checkoutAccommodationOfferView, getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { isStripeWalletInstalmentsEnabled } from 'lib/payInInstalmentsUtils'
import { isVelocityEnabled } from './featuresSelectors'
import { VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS } from 'constants/partnerships'
import { getVelocityEligibleItems } from 'checkout/selectors/payment/partnerships'
import { getFlightItems } from 'checkout/selectors/view/flights'
import shouldShowGiftCards from 'lib/shouldShowGiftCards'
import { getCarHireItems } from 'checkout/selectors/view/carHire'
import { isLEStaffOrAdmin } from './accountSelectors'
import { getDepositAmount } from 'checkout/selectors/payment/deposit'
import { isStandaloneFlights } from './flightsSelectors'

/*
  NOTE:
  This file is for the new checkout. If you're working with the current checkout,
  try `src/client/selectors/cart/cartSelectors.ts`.

  We are also currently moving all 'checkout' specific selectors into the 'checkout' app.
  New payment selectors should be added to 'src/checkout/selectors/payment'
*/

const isPostPurchaseAncillaryPayment = createSelector(
  (state) => state.checkout.cart.postPurchase,
  (postPurchase) => postPurchase === 'deposit-balance' || postPurchase === 'instalment-balance' || postPurchase === 'instalment' || postPurchase === 'reserve-balance',
)

export const getCreditBalanceForCheckoutCurrency = (state: App.State) =>
  state.auth.account.creditsByCurrency[state.checkout.cart.currencyCode]?.balance ?? 0

export const cartIncludesFlights = createSelector(
  getFlightItems,
  (flightItems) => !!flightItems.length,
)

export const cartIncludesTourV2 = (state: App.State) => state.checkout.cart.items.some(isTourV2Item)

export const isCartItemLeTour = (state: App.State) => {
  const cartItems = state.checkout.cart.items
  const tourV2Offers = state.offer.tourV2Offers
  const item = cartItems.find(isTourV2Item)
  if (!item) {
    return false
  }
  const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
  return offer?.isLeTour
}

export const getGrandTotal = (state: App.State) => checkoutTotalsView(state).data.grandTotal

// TODO: implement
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isBookDatesOnly = (state: App.State) => false

export const isStandaloneExperiences = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.every(isExperienceItem),
)

const isGiftCardPurchase = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.some(isGiftCardItem),
)

export const getCartFlightJourneys = createSelector(
  getFlightItems,
  (state: App.State) => state.flights.journeysById,
  (flightItems, journeys): App.Journey[] => {
    return flightItems.map(item => journeys[item.searchId] as App.Journey).filter(Boolean)
  },
)

// TODO: implement
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isInsuranceAccepted = (state: App.State) => false

// TODO: implement
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isDepositBalancePayment = (state: App.State) => false

export const isDepositSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.DEPOSIT

export const isFullPaymentSelected = (state: App.State) =>
  state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.FULL &&
  !isPostPurchaseAncillaryPayment(state)

export const isInstalmentsSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.INSTALMENT

export const isReserveForFreeSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.RESERVE_FOR_FREE

const getCheckoutCurrency = (state: App.State) => state.checkout.cart.currencyCode

const cartHasBedbankItem = (state: App.State) => state.checkout.cart.items.some(item => item.itemType === 'bedbankHotel')

export const isGiftCardEnabled = createSelector(
  getCheckoutCurrency,
  isGiftCardPurchase,
  isStandaloneFlights,
  (currencyCode, isGiftCard, isStandaloneFlight): boolean => {
    return shouldShowGiftCards(currencyCode) &&
      !isGiftCard &&
      !isStandaloneFlight
  },
)

/**
 * Determine whether the cart can be paid with LE credits
 *
 * @remarks
 * When the new checkout supports gift cards, this must return false when there's a gift card in the cart
 */
export const isCreditPayable = createSelector(
  getCreditPayableAmount,
  isStandaloneFlights,
  isGiftCardPurchase,
  (state: App.State) => state.checkout.payment.partnerships,
  (credit, isStandaloneFlights, isGiftCard): boolean => {
    return credit > 0 && !isStandaloneFlights && !isGiftCard
  },
)

export const isPromoCodeAllowed = createSelector(
  isStandaloneExperiences,
  getAccommodationItems,
  (state:App.State) => getCarHireItems(state),
  (state: App.State) => state.checkout.payment.partnerships,
  (state: App.State) => isLEStaffOrAdmin(state),
  isPostPurchaseAncillaryPayment,
  (isStandaloneExperiences, accommodationItems, carHireItems, partnerships, isLEStaff, isAncillaryPayment): boolean => {
    if (partnerships?.velocity?.burn?.isApplied || isAncillaryPayment) { return false }

    return isStandaloneExperiences ||
    (accommodationItems.length > 0 &&
       accommodationItems.every(item => isLEAccommodationItem(item) || isBedbankItem(item) || isTourV1Item(item) || isTourV2Item(item) || isCruiseItem(item))) ||
      ((isLEStaff) && carHireItems.length > 0)
  })

export const isNonProtectedInsurance = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items) => items.some(isNoProtectionInsuranceItem),
)

export const isLatitudePayAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
  ) =>
    isLatitudePayEnabled(currency) &&
    !bookDatesOnly &&
    cartTotal <= MAX_LATITUDE_PAY_PAYMENT &&
    !isStandaloneFlights &&
    !isGiftCard,
)

export const isLatitudeFinanceAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
  ) =>
    isLatitudeFinanceEnabled(currency) &&
    !bookDatesOnly &&
    cartTotal >= MIN_LATITUDE_PAYMENT &&
    !isStandaloneFlights &&
    !isGiftCard,
)

export const isLatitudeGemAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
  ) =>
    isLatitudeGemEnabled(currency) &&
    !bookDatesOnly &&
    cartTotal >= MIN_LATITUDE_PAYMENT &&
    !isStandaloneFlights &&
    !isGiftCard,
)

export const isPaypalAvailable = createSelector(
  getCheckoutCurrency,
  isGiftCardPurchase,
  (cartCurrency, isGiftCard) => isPaypalEnabled(cartCurrency) && !isGiftCard,
)

// For user with credits on the checkout page,
// the grand total could be > 35 while the app
// is fetching the user's credit balance (assumed
// to be 0). This causes Klarna Checkout to be
// mounted and immediately unmounted once the user
// data returns and causes the grand total to equal 0

const isKlarnaAvailable = createSelector(
  getGrandTotal,
  getCheckoutCurrency,
  cartHasBedbankItem,
  isStandaloneFlights,
  isStandaloneExperiences,
  isGiftCardPurchase,
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
  ) => !hasBedbankItem &&
    isKlarnaEnabled(currency) &&
    isWithinKlarnaAvailabilityRange(cartTotal) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard,
)

const isKlarnaBpAvailable = createSelector(
  getGrandTotal,
  getCheckoutCurrency,
  cartHasBedbankItem,
  isStandaloneFlights,
  isStandaloneExperiences,
  isGiftCardPurchase,
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
  ) => !hasBedbankItem &&
    isKlarnaBpEnabled(currency) &&
    isWithinKlarnaBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard,
)

export const isAfterPayBpAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
  ) => isAfterPayBpEnabled(currency) &&
    !bookDatesOnly &&
    isWithinAfterPayBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isGiftCard,
)

export const isHoolahBpAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  cartIncludesTourV2, // Temporarily disabled for tours, until it can be tested
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
  ) =>
    isHoolahBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinHoolahBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard,
)

export const isAtomeBpAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  cartIncludesTourV2,
  isStandaloneFlights,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
  ) =>
    isAtomeBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinAtomeBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard,
)

export const isWaaveBpAvailable = createSelector(
  getGrandTotal,
  isBookDatesOnly,
  getCheckoutCurrency,
  isStandaloneFlights,
  cartIncludesTourV2,
  isGiftCardPurchase,
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    includesTourV2,
    isGiftCard,
  ) =>
    isWaaveBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    // !isChangePackage && // Add this here once the new checkout has package change feature available
    isWithinWaaveBpAvailabilityRange(cartTotal, currency) &&
    !!config.WAAVE_BP_GLOBAL_CHECKOUT_ENABLED &&
    !isGiftCard,
)

export const isApplePayAvailable = createSelector(
  getCheckoutCurrency,
  (state: App.State) => state.config.canPayWithApplePay,
  cartIncludesFlights,
  isFullPaymentSelected,
  cartIncludesTourV2,
  isDepositSelected,
  isInstalmentsSelected,
  (currency, canPayWithApplePay, includesFlights, isFullPaymentSelected, includesTourV2, isDepositSelected, isInstalmentsSelected) => isApplePayEnabled(currency) &&
    canPayWithApplePay &&
    (!includesFlights || isStripeWalletFlightsEnabled()) &&
    (isDepositSelected ? isStripeWalletDepositsEnabled : true) &&
    (isFullPaymentSelected || !includesTourV2) &&
    (isInstalmentsSelected ? isStripeWalletInstalmentsEnabled : true),
)

export const isGooglePayAvailable = createSelector(
  getCheckoutCurrency,
  (state: App.State) => state.config.canPayWithGooglePay,
  cartIncludesFlights,
  isStandaloneFlights,
  isFullPaymentSelected,
  cartIncludesTourV2,
  isDepositSelected,
  isInstalmentsSelected,
  (currency, canPayWithGooglePay, includesFlights, isStandaloneFlights, isFullPaymentSelected, includesTourV2, isDepositSelected, isInstalmentsSelected) => isGooglePayEnabled(currency) &&
    canPayWithGooglePay &&
    (!includesFlights || isStripeWalletFlightsEnabled()) &&
    !isStandaloneFlights &&
    (isDepositSelected ? isStripeWalletDepositsEnabled : true) &&
    (isFullPaymentSelected || !includesTourV2) &&
    (isInstalmentsSelected ? isStripeWalletInstalmentsEnabled : true),
)

export const isRazorpayAvailable = createSelector(
  getCheckoutCurrency,
  isStandaloneFlights,
  cartIncludesTourV2, // Temporarily disabled for tours, until it can be tested
  (currency, isStandaloneFlights, includesTourV2) => !includesTourV2 && getPaymentMethods(currency).includes(RAZORPAY_PAYMENT_TYPE) && !isStandaloneFlights,
)

export const isStripePaymentElementCardAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  isRazorpayAvailable,
  (regionCode, razorpayAvailable) =>
    !!isStripePaymentElementCardEnabled(regionCode) && !razorpayAvailable,
)

export const isStripePaymentElementGiropayAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (regionCode) =>
    !!isStripePaymentElementGiropayEnabled(regionCode),
)

export const isStripe3dsAvailable = createSelector(
  getCheckoutCurrency,
  isStandaloneFlights,
  isGiftCardPurchase,
  (state: App.State) => state.auth.account.numberOfPurchases,
  cartHasBedbankItem,
  isRazorpayAvailable,
  isStripePaymentElementCardAvailable,
  (currency, isStandaloneFlights, isGiftCardPurchase, numberOfPurchases, hasBedbankItem, razorpayAvailable, stripePaymentElementCardAvailable) => (
    (isStandaloneFlights && isStripe3dsForStandaloneEnabled()) ||
    (isGiftCardPurchase && isStripe3dsForGiftCardsEnabled()) ||
    (numberOfPurchases < 1 && hasBedbankItem && isStripeBedbankRepeatCustomerCheckEnabled()) ||
    isStripe3dsEnabled(currency)
  ) && !razorpayAvailable && !stripePaymentElementCardAvailable,
)

export const isStripeAvailable = createSelector(
  getCheckoutCurrency,
  isStripe3dsAvailable,
  isRazorpayAvailable,
  isStripePaymentElementCardAvailable,
  (currency, stripe3dsAvailable, razorpayAvailable, stripePaymentElementCardAvailable) => getPaymentMethods(currency).includes(STRIPE_PAYMENT_TYPE) && !stripe3dsAvailable && !razorpayAvailable && !stripePaymentElementCardAvailable,
)

export const isGoCardlessAvailable = createSelector(
  getCheckoutCurrency,
  (state: App.State) => state.auth.account.email,
  (currency, email) => {
    // In substitute of AB test, remove after test
    return config.ENABLE_GO_CARDLESS_PAYMENT && getPaymentMethods(currency).includes(GO_CARDLESS_PAYMENT_TYPE) && email && email.length % 4 === 0
  },
)

export const isVelocityAvailable = createSelector(
  getCheckoutCurrency,
  isVelocityEnabled,
  getVelocityEligibleItems,
  isStandaloneFlights,
  (currency, isVelocityEnabled, velocityEligibleItems, isStandaloneFlights): boolean =>
    currency === 'AUD' &&
    isVelocityEnabled &&
    !isStandaloneFlights &&
    velocityEligibleItems.length > 0,
)

export const getPaymentMethodAvailability = createSelector(
  isFullPaymentSelected,
  (state: App.State) => state.checkout.promo,
  isStripeAvailable,
  isLatitudeFinanceAvailable,
  isLatitudeGemAvailable,
  isPaypalAvailable,
  isRazorpayAvailable,
  isLatitudePayAvailable,
  isKlarnaAvailable,
  isKlarnaBpAvailable,
  isAfterPayBpAvailable,
  isHoolahBpAvailable,
  isGooglePayAvailable,
  isApplePayAvailable,
  isStripe3dsAvailable,
  isAtomeBpAvailable,
  isWaaveBpAvailable,
  isVelocityAvailable,
  isStripePaymentElementCardAvailable,
  isStripePaymentElementGiropayAvailable,
  isGoCardlessAvailable,
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  isReserveForFreeSelected,
  (
    isFullPaymentSelected,
    promotion,
    stripeAvailable,
    latitudeFinanceAvailable,
    latitudeGemAvailable,
    paypalAvailable,
    razorpayAvailable,
    latitudePayAvailable,
    klarnaAvailable,
    klarnaBpAvailable,
    afterPayBpAvailable,
    hoolahBpAvailable,
    googlePayAvailable,
    applePayAvailable,
    stripe3dsAvailable,
    atomeBpAvailable,
    waaveBpAvailable,
    isVelocityAvailable,
    stripePaymentElementCardAvailable,
    stripePaymentElementGiropayAvailable,
    goCardlessAvailable,
    isVelocityApplied,
    isReserveForFreeSelected,
  ): Record<PaymentMethod, boolean> => {
    const paymentMethodAvailabilities = {
      stripe: stripeAvailable && isFullPaymentSelected,
      stripe_3ds: stripe3dsAvailable,
      stripe_payment_element_card: stripePaymentElementCardAvailable,
      stripe_payment_element_giropay: stripePaymentElementGiropayAvailable && isFullPaymentSelected,
      latitude: latitudeFinanceAvailable && isFullPaymentSelected,
      latitude_gem: latitudeGemAvailable && isFullPaymentSelected,
      braintree: paypalAvailable && isFullPaymentSelected,
      razorpay: razorpayAvailable && isFullPaymentSelected,
      latitude_pay: latitudePayAvailable && isFullPaymentSelected,
      klarna: klarnaAvailable && isFullPaymentSelected,
      klarna_bp: klarnaBpAvailable && isFullPaymentSelected,
      afterpay_bp: afterPayBpAvailable && isFullPaymentSelected,
      hoolah_bp: hoolahBpAvailable && isFullPaymentSelected,
      velocity: isVelocityAvailable && isFullPaymentSelected,
      googlepay: googlePayAvailable && !isReserveForFreeSelected,
      applepay: applePayAvailable && !isReserveForFreeSelected,
      atome_bp: atomeBpAvailable && isFullPaymentSelected,
      waave_bp: waaveBpAvailable && isFullPaymentSelected,
      gocardless: goCardlessAvailable && isFullPaymentSelected,
    }

    if (isVelocityApplied) {
      for (const key of Object.keys(paymentMethodAvailabilities)) {
        if (VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(key)) {
          continue
        }
        paymentMethodAvailabilities[key] = false
      }
    }

    const applyPromotionModifiers = promotion && promotion.allowedPaymentMethods.length > 0
    const applyPartnershipModifiers = isVelocityApplied

    return mapObject(paymentMethodAvailabilities, (value, payMethod: string) => {
      let result = value

      if (applyPromotionModifiers) {
        result = result && promotion.allowedPaymentMethods.includes(payMethod)
      }

      if (applyPartnershipModifiers) {
        result = result && VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(payMethod)
      }

      return result
    })
  },
)

export const isAnyInstalmentPayAvailable = createSelector(
  isLatitudePayAvailable,
  isLatitudeFinanceAvailable,
  isLatitudeGemAvailable,
  isKlarnaAvailable,
  isAfterPayBpAvailable,
  isKlarnaBpAvailable,
  isHoolahBpAvailable,
  isAtomeBpAvailable,
  (latitudePay, latitudeFinance, latitudeGem, klarna, afterPayBp, klarna_bp, hoolah_bp, atome_bp) => latitudePay || latitudeFinance || latitudeGem || klarna || afterPayBp || klarna_bp || hoolah_bp || atome_bp,
)

const getTourV2StripeMetadata = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.tourV2Offers,
  (cartItems, tourV2Offers) => {
    const item = cartItems.find(isTourV2Item)
    if (!item) { return {} }
    const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]

    const variation = offer?.variations[item.purchasableOption.fkVariationId]

    return {
      deal_country: variation?.countriesVisited?.[0],
      deal_destination: variation?.startLocation,
      lead_time: dateDifference(new Date(item.startDate), beginningOfToday()).days,
      product_type: 'tour_v2',
    }
  },
)

const getCruiseStripeMetadata = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.cruise.cruiseOffers,
  (cartItems, cruiseOffers) => {
    const item = cartItems.find(isCruiseItem)

    if (!item) { return {} }

    const offer = cruiseOffers[item.offerId]

    return {
      deal_destination: offer.departurePort,
      lead_time: dateDifference(new Date(item.startDate), beginningOfToday()).days,
      product_type: 'cruise',
    }
  },
)

const getExperienceStripeMetadata = createSelector(
  isStandaloneExperiences,
  getExperienceItems,
  (state: App.State) => state.experience.experiences,
  (isStandaloneExperiences, cartItems, experiences) => {
    if (isStandaloneExperiences) {
      const item = cartItems[0]
      const experience = experiences[item.experienceId]
      return experience.hasCalendar ? {
        lead_time: dateDifference(new Date(item.date), beginningOfToday()).days,
        product_type: 'experience',
      } : {
        product_type: 'experience',
      }
    } else {
      return {}
    }
  },
)

const getAccommodationStripeMetadata = createSelector(
  checkoutAccommodationOfferView,
  (offerView: App.WithDataStatus<Array<App.Checkout.AccommodationOfferView>>) => {
    if (offerView.data.length) {
      const offerItem = offerView.data[0]
      return {
        lead_time: dateDifference(new Date(offerItem.startDate), beginningOfToday()).days,
        ...getOfferCountryAndDestination(offerItem.offer),
        ...('refundable' in offerItem && { is_refundable: offerItem.refundable }),
      }
    }
  },
)

function getOfferCountryAndDestination(offer: App.Offer | App.BedbankOffer | Tours.V2Offer | App.CruiseOffer) {
  if (isLEOffer(offer)) {
    return {
      deal_country: offer.property?.geoData?.countryCode,
      deal_destination: offer.property?.geoData?.administrativeAreaLevel1,
    }
  }
  if (isBedbank(offer)) {
    return {
      deal_country: offer.property?.address?.countryCode,
      deal_destination: offer.property?.address?.city,
    }
  }
}

const getProductType = createSelector(
  (state: App.State) => state.checkout.cart.items,
  checkoutAccommodationOfferView,
  (items: App.Checkout.Item[], offerView): string => {
    if (items.some(isExperienceItem)) {
      return 'experience'
    } else if (items.some(isInsuranceProtectionItem)) {
      return 'insurance'
    } else if (items.some(isAccommodationItem)) {
      return offerView.data[0].offerType
    } else if (items.some(isFlightItem)) {
      return 'flight'
    } else if (items.some(isCarHireItem)) {
      return 'car-hire'
    }
    return ''
  },
)

export const prepareAdditionalInfoForStripe = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => state.utm,
  (state: App.State) => state.checkout.cart.currencyCode,
  getProductType,
  getAccommodationStripeMetadata,
  getTourV2StripeMetadata,
  getCruiseStripeMetadata,
  getExperienceStripeMetadata,
  (account, utm, cartCurrency, productType, accommodationStripMetadata, tourV2StripeMetadata, cruiseStripeMetadata, experienceStripeMetadata): any => {
    const commonMetaData = {
      spoofed_customer: !!account.isSpoofed,
      utm_source: utm.source,
      utm_medium: utm.medium,
      is_bnbl: false, // TODO: add logic for this when the new checkout can handle BNBL offers
      order_currency: cartCurrency,
    }

    // TODO: handle other product types

    return {
      ...commonMetaData,
      ...accommodationStripMetadata,
      ...experienceStripeMetadata,
      ...tourV2StripeMetadata,
      ...cruiseStripeMetadata,
      product_type: productType,
    }
  },
)

export const getPaypalSupplementaryData = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.tourV2Offers,
  (cartItems, offers, bedbankOffers, tourV2Offers): PaypalSupplementaryData => {
    const leHotelItem = cartItems.find(isLEHotelItem)
    const bedbankItem = cartItems.find(isBedbankItem)
    const tourV1Item = cartItems.find(isTourV1Item)
    const tourV2Item = cartItems.find(isTourV2Item)

    const toISO601Format = date => moment.utc(date).format('YYYY-MM-DDT hh:mm:ss.SSSZ')
    if (tourV1Item) {
      const pkg = offers[tourV1Item.offerId]?.packages.find(p => p.id === tourV1Item.packageId)
      return {
        ota_service_start_date: toISO601Format(tourV1Item.startDate),
        ota_service_end_date: toISO601Format(tourV1Item.endDate),
        ota_start_country: pkg?.tour.geoData.countryCode,
      }
    }
    else if (tourV2Item) {
      const offer = tourV2Offers[tourV2Item.offerId]
      const variation = offer?.variations[tourV2Item.purchasableOption.fkVariationId]
      return {
        ota_service_start_date: toISO601Format(tourV2Item.startDate),
        ota_service_end_date: toISO601Format(tourV2Item.endDate),
        ota_start_country: variation?.countriesVisited?.[0],
      }
    }
    else if (leHotelItem) {
      const offer = offers[leHotelItem.offerId]
      const supplementaryData = {
        ota_start_country: offer?.property.geoData.countryCode,
      } as PaypalSupplementaryData
      if (leHotelItem.reservationType === 'instant_booking') {
        supplementaryData.ota_service_start_date = toISO601Format(leHotelItem.checkIn)
        supplementaryData.ota_service_end_date = toISO601Format(leHotelItem.checkOut)
      }
      return supplementaryData
    }
    else if (bedbankItem) {
      const offer = bedbankOffers[bedbankItem.offerId]
      return {
        ota_service_start_date: toISO601Format(bedbankItem.checkIn),
        ota_service_end_date: toISO601Format(bedbankItem.checkOut),
        ota_start_country: offer?.property.address.countryCode,
      }
    }
    return {}
  },
)

function getHotelItineraryForKlarna(
  offerView: App.Checkout.LEAccommodationOfferView,
  offer: App.Offer,
  account: App.AuthAccount,
): KlarnaAttachment['hotel_reservation_details'][number]['hotel_itinerary'][number] {
  return {
    hotel_name: offer.property.name,
    address: {
      street_address: offer.property.address || undefined,
      city: offer.property.geoData.locality || undefined,
      country: offer.property.geoData.country || undefined,
    },
    start_time: offerView.reservationType === RESERVATION_TYPE_INSTANT_BOOKING ? moment.utc(offerView.startDate).format() : undefined,
    end_time: offerView.reservationType === RESERVATION_TYPE_INSTANT_BOOKING ? moment.utc(offerView.endDate).format() : undefined,
    number_of_rooms: offerView.itemViews.length,
    ticket_delivery_method: 'email',
    ticket_delivery_recipient: account.email,
    // TODO:
    // hotel_price: accommodation.package.price * 100,
  }
}

function getTourV2ItineraryForKlarna(
  offerView: App.Checkout.TourV2AccommodationOfferView,
  offer: Tours.V2Offer,
): KlarnaAttachment['voucher'][number] {
  return {
    voucher_name: offer.name,
    voucher_company: offer.brand.name,
    start_time: moment.utc(offerView.startDate).format(),
    end_time: moment.utc(offerView.endDate).format(),
  }
}

function getCruiseItineraryForKlarna(
  offerView: App.Checkout.CruiseAccommodationOfferView,
  offer: App.CruiseOffer,
): KlarnaAttachment['voucher'][number] {
  return {
    voucher_name: offer.name,
    voucher_company: offer.cruiseLine.name,
    start_time: moment.utc(offerView.startDate).format(),
    end_time: moment.utc(offerView.endDate).format(),
  }
}

function getExperienceVoucherForKlarna(
  experienceView: App.Checkout.ExperienceItemView,
): KlarnaAttachment['voucher'][number] {
  return experienceView.hasCalendar ? {
    voucher_name: experienceView.title,
    start_time: moment.utc(experienceView.bookingDate).format(),
    end_time: moment.utc(experienceView.bookingDate).format(),
  } : {
    voucher_name: experienceView.title,
  }
}

export const getKlarnaSessionAttachment = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.cruise.cruiseOffers,
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  (state: App.State) => state.auth.account,
  (offers, tourV2Offers, cruiseOffers, accommodationOfferViews, experienceItemViews, account) => {
    const result: KlarnaAttachment = {
      customer_account_info: [{
        unique_account_identifier: account.email,
      }],
    }

    const vouchers: KlarnaAttachment['voucher'] = []

    // LE hotels
    const leHotelOfferViews = accommodationOfferViews.data.filter(view => isLEHotelItem(view.itemViews[0].item)) as Array<App.Checkout.LEAccommodationOfferView>
    if (leHotelOfferViews.length > 0) {
      result.hotel_reservation_details = [{
        hotel_itinerary: leHotelOfferViews.map(offerView => {
          const offer = offers[offerView.offerId]
          return getHotelItineraryForKlarna(offerView, offer, account)
        }),
      }]
    }

    // Tour V2
    const tourV2OfferViews = accommodationOfferViews.data.filter(view => isTourV2Item(view.itemViews[0].item)) as Array<App.Checkout.TourV2AccommodationOfferView>
    if (tourV2OfferViews.length > 0) {
      // Klarna attachment schema has no field for tours. Voucher field is the best fit.
      vouchers.push(
        ...tourV2OfferViews.map(offerView => {
          const offer = tourV2Offers[offerView.offerId]
          return getTourV2ItineraryForKlarna(offerView, offer)
        }),
      )
    }

    // Cruise
    const cruiseOfferViews = accommodationOfferViews.data.filter(view => isCruiseItem(view.itemViews[0].item)) as Array<App.Checkout.CruiseAccommodationOfferView>
    if (cruiseOfferViews.length > 0) {
      // Klarna attachment schema has no field for tours. Voucher field is the best fit.
      vouchers.push(
        ...cruiseOfferViews.map(offerView => {
          const offer = cruiseOffers[offerView.offerId]
          return getCruiseItineraryForKlarna(offerView, offer)
        }),
      )
    }

    // Experiences
    if (experienceItemViews.data.length > 0) {
      vouchers.push(
        ...experienceItemViews.data.map(getExperienceVoucherForKlarna),
      )
    }

    if (vouchers.length > 0) {
      result.voucher = vouchers
    }

    // TODO: Flights and insurance

    return {
      body: JSON.stringify(result),
      content_type: 'application/vnd.klarna.internal.emd-v2+json',
    }
  },
)

export const getPaymentAmount = createSelector(
  isDepositSelected,
  getDepositAmount,
  checkoutTotalsView,
  isInstalmentsSelected,
  (isDepositSelected,
    depositAmount,
    totals,
    isInstalmentsSelected,
  ) => {
    if (isDepositSelected || isInstalmentsSelected) return depositAmount
    return totals.data.payableNow
  },
)
